import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';
import styled from 'styled-components';

import { CheckSvg } from '../../assets/icons/CheckSvg';
import curiLogo from '../../assets/images/app/curiLogo.svg';
import loadingStars from '../../assets/images/app/loaderstars.svg';
import theme from '../../assets/theme';
import { useDesignerDataContext } from '../../hooks/useDesignerLogic';
import useGamePin from '../../hooks/useGamePin';
import { useLoaderState } from '../../hooks/useLoaderState';
import { formatPin } from '../../utils/generationUtils';

type StepState = 'pending' | 'loading' | 'completed';

const SlideElementLoader = ({ gameId }: { gameId?: string }) => {
  const { t } = useTranslate();

  // If gameId is not provided (edit mode), try to get it from the designer context
  const activeGameId = useDesignerDataContext(
    context => context.state.localTemplate?.content.activeGame?.id,
  );
  const effectiveGameId = gameId || activeGameId;

  const { pin } = useGamePin(effectiveGameId);

  const localSteps = useMemo(() => {
    return [
      t('Loader.Pin', 'Generating PIN for students to easily join your activity'),
      t('Loader.Tailoring', 'Tailoring activity to your students.'),
      t('Loader.Moderation', 'Activating Auto AI Moderation of student responses.'),
      t(
        'Loader.Designer',
        "Creating your slides. Everything is editable once it's done loading.",
      ),
    ];
  }, [t]);

  const { stepStates, showPin } = useLoaderState(effectiveGameId);

  return (
    <LoaderContainer>
      <StepsContainer>
        {localSteps.map((step, index) => (
          <StepRow key={index}>
            <IconContainer>
              {stepStates[index] === 'loading' && <MagicPulsingLoader />}
              <StepIcon $state={stepStates[index]}>
                <CheckIconWrapper $state={stepStates[index]}>
                  <CheckSvg />
                </CheckIconWrapper>
              </StepIcon>
            </IconContainer>
            <StepText
              $isActive={stepStates[index] === 'loading'}
              $isCompleted={stepStates[index] === 'completed'}
            >
              {index === 0 && showPin ? (
                <>
                  {step.replace(/\.$/, '')}:{' '}
                  <PinText
                    $shouldAnimate={
                      stepStates[0] === 'completed' && stepStates[1] === 'loading'
                    }
                  >
                    {formatPin(pin)}
                  </PinText>
                </>
              ) : (
                step
              )}
            </StepText>
          </StepRow>
        ))}
      </StepsContainer>
    </LoaderContainer>
  );
};

const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgb(250, 232, 218);
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  width: 90%;
  max-width: 90%;
  z-index: 1;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  min-height: 120px;
  height: 120px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  position: relative;
`;

const StepIcon = styled.div<{ $state: StepState }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.5s ease-in-out, box-shadow 0.5s ease-in-out;
  background-color: ${({ $state }) =>
    $state === 'completed'
      ? theme.colors.orange
      : $state === 'loading'
      ? 'transparent'
      : theme.colors.grey};
  box-shadow: ${({ $state }) =>
    $state === 'completed'
      ? '0 0px 16px rgba(255, 255, 255, 0.5)'
      : $state === 'loading'
      ? 'none'
      : 'inset 0 0px 8px rgba(0, 0, 0, 0.25)'};
  flex-shrink: 0;
  overflow: hidden;
`;

const CheckIconWrapper = styled.div<{ $state: StepState }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-in-out;
  z-index: 2;
  pointer-events: none;

  svg {
    width: 40px;
    height: 40px;
    transition: inherit;
  }

  ${({ $state }) => {
    if ($state === 'completed') {
      return `
        transform: scale(1) rotate(0deg);
        opacity: 1;
      `;
    } else {
      return `
        transform: scale(0.1) rotate(180deg);
        opacity: 0;
      `;
    }
  }}
`;

const MagicPulsingLoader = styled.div`
  width: 80px;
  height: 80px;
  aspect-ratio: 1;
  border-radius: 50%;
  box-shadow: 0 0 0 0 ${theme.colors.translucentOrange};
  animation: l2 1.5s infinite linear;
  position: absolute;
  top: 0;
  left: 0;
  background: center / contain no-repeat url(${curiLogo.src});
  z-index: 1;

  &:before,
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: 0 0 0 0 ${theme.colors.translucentOrange};
    animation: inherit;
    animation-delay: -0.5s;
  }
  &::after {
    content: '';
    position: absolute;
    inset: -40px;
    border-radius: inherit;
    background: url(${loadingStars.src}) no-repeat;
    background-size: 100% 100%;
    animation: pulse-stars 1.5s infinite linear;
    opacity: 0;
  }
  &:after {
    animation-delay: -1s;
  }
  @keyframes l2 {
    100% {
      box-shadow: 0 0 0 80px rgba(255, 255, 255, 0.1);
    }
  }
  @keyframes pulse-stars {
    0% {
      transform: scale(0.5);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.5);
      opacity: 1;
    }
    100% {
      transform: scale(2.5);
      opacity: 0;
    }
  }
`;

const StepText = styled.p<{ $isActive: boolean; $isCompleted: boolean }>`
  margin: 0;
  font-size: 36px;
  font-weight: ${({ $isActive }) => ($isActive ? '800' : '700')};
  color: ${({ $isActive, $isCompleted }) =>
    $isActive
      ? theme.colors.black
      : $isCompleted
      ? theme.colors.black
      : theme.colors.iconGrey};
  line-height: 1.4;
  flex: 1;
  display: flex;
  align-items: center;
  transition: color 0.2s ease-in-out;
`;

const PinText = styled.strong<{ $shouldAnimate: boolean }>`
  font-size: 60px;
  font-weight: 800;
  margin-left: 8px;
  ${({ $shouldAnimate }) =>
    $shouldAnimate && `animation: popIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);`}

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.3);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export default SlideElementLoader;
